import { AppThunk } from '@store/types';
import { FinishedReschedulingPayload } from '@reducers/v2/planboard/types';
import { fetchMachineOperations } from './fetchMachineOperations';
import {
  setColumnRescheduling,
  clearPendingResetOperation,
  addMachineToRefetchQueue,
  clearReschedulingByCorrelationId,
} from '../planboard.slice';
import { MACHINES_PER_REQUEST, RESET_REFETCH_DELAY_MS } from '@v2/pages/planboard/constants';

// the thunk that handles finished rescheduling events for all users, refetching affected columns
// handles the OnFinishedReschedulingProductionOperations SignalR event.
export const handleFinishedRescheduling =
  (payload: FinishedReschedulingPayload): AppThunk =>
  (dispatch, getState) => {
    const { requestCorrelationId, statistics } = payload;
    const { planboard } = getState();

    // 1. Handle initiator-specific cleanup (if correlationId matches)
    if (requestCorrelationId && planboard.pendingResetOperations[requestCorrelationId]) {
      const { machineId } = planboard.pendingResetOperations[requestCorrelationId];

      // Clean up pending operation tracking
      dispatch(clearPendingResetOperation(requestCorrelationId));

      // Signal to hook that completion happened (so it can clear timeout/local state)
      dispatch(addMachineToRefetchQueue(machineId));

      // Keep setColumnRescheduling(true) active — it serves as both:
      // a) visual loading indicator during refetch
      // b) guard for onRescheduledProductionOperations to skip this machine
      // Loading state is cleared after refetch completes (see below).
    }

    // 1b. Clear position-based rescheduling loading (cross-column moves)
    if (requestCorrelationId) {
      dispatch(clearReschedulingByCorrelationId(requestCorrelationId));
    }

    // 2. Refetch ALL affected columns for ALL users
    if (statistics?.length) {
      const resetMachineId =
        requestCorrelationId && planboard.pendingResetOperations[requestCorrelationId]
          ? planboard.pendingResetOperations[requestCorrelationId].machineId
          : null;

      statistics.forEach(({ machineId, scheduledProductionOperationsCount }) => {
        // Only refetch if: column was affected AND is currently loaded
        if (scheduledProductionOperationsCount > 0 && planboard.machineProductionOperations[machineId]) {
          if (machineId === resetMachineId) {
            // Reset-priority: SignalR updates are blocked for this machine (reschedulingMachineIds guard),
            // so we must refetch. Delay allows search index to catch up.
            setTimeout(() => {
              dispatch(fetchMachineOperations({ machineId, top: MACHINES_PER_REQUEST })).then(() => {
                dispatch(setColumnRescheduling({ machineId, isRescheduling: false }));
              });
            }, RESET_REFETCH_DELAY_MS);
          }
          // For non-reset operations (regular D&D): no refetch needed.
          // OnRescheduledProductionOperations already updated all affected items in-memory
          // with correct positions. Refetching would overwrite them with stale search index data.
        }
      });
    }
  };
